import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import {
  Account,
  AdminAccount,
  HomeResponse,
  HomeUpdateRequest,
  ImpersonationRequest,
  ImpersonationResponse,
  NewAccount,
} from '@/api/generated'
import {
  AdministrationState,
  EmptyAdministrationState,
} from '@/business/administration/store/administration-state'
import {
  CREATE_ACCOUNT,
  FETCH_ACCOUNTS,
  FETCH_HOME,
  IMPERSONATE,
  RESET_PASSWORD,
  UPDATE_HOME,
} from '@/business/administration/store/types'
import { Api } from '@/api/api'
import { Loading } from '@/shared/utils/vuex-loading'
import { Injector } from '@/config/dependency-injection'
import { TokenHolder } from '@/shared/utils/token-holder'
import { AdminTokenHolder } from '@/config/injection-tokens'
import { AuthenticationModule } from '@/authentication/store/authentication-module'
import { LOGIN } from '@/authentication/store/types'
import { GetAccountsPayload } from '@/business/administration/store/models/get-accounts-payload'
import { updateOrPrepend } from '@/shared/utils/list'
import { CreateAccountPayload } from '@/business/administration/store/models/create-account-payload'
import { ResetPasswordPayload } from '@/business/administration/store/models/reset-password-payload'

const STORE_NAME = 'administration'

@Module({
  name: STORE_NAME,
})
export class AdministrationModule extends VuexModule {
  public static readonly STORE_NAME: string = STORE_NAME

  public data: AdministrationState = EmptyAdministrationState

  @Mutation
  public [CREATE_ACCOUNT](response: NewAccount): void {
    this.data.accounts = updateOrPrepend(this.data.accounts, response.account)
  }

  @Mutation
  public [FETCH_ACCOUNTS](accounts: AdminAccount[]) {
    this.data.accounts = accounts
  }

  @Mutation
  public [FETCH_HOME]({ offers, organisations }: HomeResponse) {
    this.data.offers = offers
    this.data.organisations = organisations
  }

  @Mutation
  public [IMPERSONATE](token: string) {
    this.data.impersonatingToken = token
  }

  @Mutation
  public [UPDATE_HOME]({ offers, organisations }: HomeResponse) {
    this.data.offers = offers
    this.data.organisations = organisations
  }

  @Loading(CREATE_ACCOUNT)
  @Action({
    commit: CREATE_ACCOUNT,
    rawError: true,
  })
  public createOrganisation({ email, name }: CreateAccountPayload): Promise<NewAccount> {
    return Api.Admin.createAccount({
      createAccount: {
        email,
        name,
      },
    })
  }

  @Loading(FETCH_ACCOUNTS)
  @Action({
    commit: FETCH_ACCOUNTS,
    rawError: true,
  })
  public fetchAccounts(payload?: GetAccountsPayload): Promise<AdminAccount[]> {
    return Api.Admin.getAccounts({
      includeDeleted: payload?.includeDeleted,
      organisationId: payload?.organisationId,
    })
  }

  @Action({
    commit: FETCH_HOME,
    rawError: true,
  })
  public fetchHome(payload: { project: string }) {
    return Api.Frontend.getHome({
      project: payload.project,
    })
  }

  @Loading(IMPERSONATE)
  @Action({
    commit: IMPERSONATE,
    rawError: true,
  })
  public impersonateAccount(payload: ImpersonationRequest): Promise<Account> {
    const tokenHolder = Injector.get(TokenHolder)
    const adminTokenHolder = Injector.get<TokenHolder>(AdminTokenHolder)

    tokenHolder.ifPresent(token => {
      adminTokenHolder.set(token)
    })

    return Api.Authentication.impersonateUser({
      impersonationRequest: payload,
    }).then((response: ImpersonationResponse) => {
      tokenHolder.set(response.token)

      Injector.get(AuthenticationModule)[LOGIN](response.account)

      return response.account
    })
  }

  @Loading(RESET_PASSWORD)
  @Action({
    rawError: true,
  })
  public resetPassword({ accountId, password }: ResetPasswordPayload): Promise<void> {
    return Api.Admin.resetAccountPassword({
      accountId,
      adminResetPasswordRequest: {
        password,
      },
    })
  }

  @Action({
    rawError: true,
  })
  public stopImpersonating() {
    const tokenHolder = Injector.get(TokenHolder)
    const adminTokenHolder = Injector.get<TokenHolder>(AdminTokenHolder)

    adminTokenHolder.ifPresent(token => {
      tokenHolder.set(token)
    })

    adminTokenHolder.clear()

    return Injector.get(AuthenticationModule).fetchLogin()
  }

  @Action({
    commit: UPDATE_HOME,
    rawError: true,
  })
  public updateHome(payload: HomeUpdateRequest) {
    return Api.Frontend.updateHome({
      homeUpdateRequest: payload,
    })
  }
}
